import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useContext,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { DataRoomBuySideGroup } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { Button, Collapsible } from 'ncoded-component-library';
import ChevrondownIcon from 'icons/Chevrondown.icon';
import MembersTable from '../../MembersTable';
import { getIsNewMember, getUserFullName } from 'utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import SettingsIcon from 'icons/Settings.icon';
import ManageBuyerGroup from '../../ManageBuyerGroup';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import { NewTag } from 'router/subrouters/DataRoom/components/NewTag';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';

import './BuySideInstance.styles.scss';

type BuySideInstanceProps = {
  className?: string;
  group: DataRoomBuySideGroup;
  showDiscoveryFeature: boolean;
  showEditGroupButton?: boolean;
};

const BuySideInstance: ForwardRefRenderFunction<
  CollapsibleRef,
  BuySideInstanceProps
> = (props, ref) => {
  const {
    className,
    group,
    showDiscoveryFeature = true,
    showEditGroupButton = true,
  } = props;
  const { dataRoomMembers, name } = group;
  const classes = classNames('buy-side-instance', className);

  const [collapsible, collapsibleRef] = useCallbackRef<CollapsibleRef>(null);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { doesntHaveAccess } = useContext(DataRoomContext);

  useImperativeHandle(ref, () => collapsible, [collapsible]);

  const lead = useMemo(
    () => dataRoomMembers.find(({ isLead }) => isLead),
    [dataRoomMembers],
  );

  const hasNewMember = useMemo(
    () => !!dataRoomMembers.find(getIsNewMember),
    [dataRoomMembers],
  );

  return (
    <div className={classes}>
      <Collapsible
        ref={collapsibleRef}
        defaultOpen={hasNewMember || showDiscoveryFeature}
        trigger={
          <div className="trigger">
            <div className="trigger__title">
              <p className="name">{name}</p>
              <Counter
                count={dataRoomMembers?.length}
                variant={COUNTER_VARIANTS.GREY}
              />
              {lead && (
                <p className="lead">
                  {getUserFullName(lead.firstName, lead.lastName)}
                </p>
              )}
              {hasNewMember && <NewTag variant="green" time={new Date()} />}
            </div>

            <div className="trigger__actions">
              {!doesntHaveAccess && showEditGroupButton && (
                <Button
                  icon={SettingsIcon}
                  onClick={(e) => {
                    e.stopPropagation();
                    drawer?.setOpen(true);
                  }}
                />
              )}
              <ChevrondownIcon />
            </div>
          </div>
        }
      >
        <MembersTable
          isBuySide
          showDiscoveryFeature={showDiscoveryFeature && collapsible?.open}
          members={dataRoomMembers}
        />
      </Collapsible>
      <ManageBuyerGroup ref={drawerRef} group={group} />
    </div>
  );
};

export default forwardRef(BuySideInstance);
